import { Fragment, useEffect, useState } from "react";
import { CheckCircle2, AlertCircle, FileText, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, fmtRp, fmtNum, fmtDate, todayISO, downloadBlob, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const Section = ({ title, rows, total, testId, detail }) => (
  <div data-testid={testId}>
    <div className="text-xs font-bold uppercase tracking-wider text-sky-700 bg-sky-50 px-4 py-2 rounded-lg">{title}</div>
    <table className="w-full text-sm mt-1">
      <tbody>
        {rows.map((r) => (
          <Fragment key={r.id}>
            <tr className="border-b border-dashed border-sky-100">
              <td className="px-4 py-2 text-slate-700"><span className="font-mono text-xs text-slate-400 mr-2">{r.code}</span>{r.name}</td>
              <td className={`px-4 py-2 text-right font-mono ${r.amount < 0 ? "text-rose-600" : ""}`}>{fmtRp(r.amount)}</td>
            </tr>
            {r.code === "1-1300" && detail}
          </Fragment>
        ))}
        <tr className="font-bold"><td className="px-4 py-2.5">Total {title}</td><td className="px-4 py-2.5 text-right font-mono">{fmtRp(total)}</td></tr>
      </tbody>
    </table>
  </div>
);

const InventoryDetail = ({ d }) => {
  const [open, setOpen] = useState(false);
  const [exp, setExp] = useState({});
  if (!d.inventory_detail?.length) return null;
  return (
    <tr><td colSpan={2} className="px-4 pb-2">
      <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 hover:underline" data-testid="bs-inventory-toggle">
        <ChevronRight className={`h-3 w-3 transition-transform ${open ? "rotate-90" : ""}`} /> Rincian nilai persediaan fisik: {fmtRp(d.inventory_value)} ({d.inventory_detail.length} jenis ikan)
      </button>
      {open && (
        <div className="mt-1.5 rounded-lg border border-sky-100 bg-sky-50/40 text-xs" data-testid="bs-inventory-detail">
          {d.inventory_detail.map((s) => (
            <div key={s.species_name} className="border-b border-dashed border-sky-100 last:border-0">
              <button type="button" onClick={() => setExp((p) => ({ ...p, [s.species_name]: !p[s.species_name] }))} className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-sky-50" data-testid={`bs-inv-species-${s.species_name}`}>
                <span className="inline-flex items-center gap-1 font-semibold text-slate-700"><ChevronRight className={`h-3 w-3 text-slate-400 transition-transform ${exp[s.species_name] ? "rotate-90" : ""}`} />{s.species_name} <span className="text-slate-400 font-normal">• {s.batches.length} batch{s.unpriced ? ` • ${s.unpriced} tanpa harga` : ""}</span></span>
                <span className="font-mono">{fmtRp(s.value)}</span>
              </button>
              {exp[s.species_name] && s.batches.map((b) => (
                <div key={b.batch_code} className="flex items-center justify-between pl-8 pr-3 py-1 text-slate-600">
                  <span><span className="font-mono text-sky-800">{b.batch_code}</span> {b.size} • {fmtNum(b.quantity_mc)} {b.unit}{b.unit_cost ? ` × ${fmtRp(b.unit_cost)}` : ""}</span>
                  <span className="font-mono">{b.value != null ? fmtRp(b.value) : <span className="text-slate-400">—</span>}</span>
                </div>
              ))}
            </div>
          ))}
          <div className="px-3 py-1.5 text-[10px] text-slate-500">Nilai fisik = Σ(harga beli × stok). Dapat berbeda dari saldo akun Persediaan bila ada batch tanpa harga atau koreksi harga manual.</div>
        </div>
      )}
    </td></tr>
  );
};

export function BalanceSheetTab() {
  const [asOf, setAsOf] = useState(todayISO());
  const [d, setD] = useState(null);
  useEffect(() => { if (asOf) api.get("/accounting/balance-sheet", { params: { as_of: asOf } }).then((r) => setD(r.data)); }, [asOf]);
  const exportPdf = async () => {
    try { const r = await api.get("/accounting/export", { params: { report: "balance", as_of: asOf }, responseType: "blob" }); downloadBlob(r.data, `neraca_${asOf}.pdf`); toast.success("PDF Neraca diunduh"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="acc-balance">
      <div className="card-aqua p-4 mb-4 flex flex-wrap items-center gap-3 rise rise-1">
        <span className="text-sm text-slate-500">Neraca per tanggal</span>
        <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-44" data-testid="input-balance-date" />
        {d && (
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${d.balanced ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="balance-status">
            {d.balanced ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}{d.balanced ? "Neraca seimbang" : "Neraca tidak seimbang"}
          </span>
        )}
        <Button className="ml-auto rounded-full bg-sky-600 hover:bg-sky-700" disabled={!d} onClick={exportPdf} data-testid="btn-export-balance-pdf"><FileText className="h-4 w-4 mr-1" /> Export PDF</Button>
      </div>
      {d && (
        <div className="card-aqua p-6 rise rise-2">
          <div className="text-center mb-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">PT Cahaya Fish Bali</div>
            <h3 className="text-xl font-bold text-slate-900">Neraca (Laporan Posisi Keuangan)</h3>
            <div className="text-sm text-slate-500">Per {fmtDate(d.as_of)}</div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <Section title="Aset" rows={d.assets} total={d.total_assets} testId="bs-assets" detail={<InventoryDetail d={d} />} />
              <div className="mt-4 flex justify-between items-center bg-slate-900 text-white rounded-xl px-4 py-3 font-bold"><span>TOTAL ASET</span><span className="font-mono" data-testid="bs-total-assets">{fmtRp(d.total_assets)}</span></div>
            </div>
            <div className="space-y-6">
              <Section title="Kewajiban" rows={d.liabilities} total={d.total_liabilities} testId="bs-liabilities" />
              <Section title="Ekuitas" rows={d.equity} total={d.total_equity} testId="bs-equity" />
              <div className="flex justify-between items-center bg-slate-900 text-white rounded-xl px-4 py-3 font-bold"><span>TOTAL KEWAJIBAN + EKUITAS</span><span className="font-mono" data-testid="bs-total-le">{fmtRp(d.total_liabilities_equity)}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
